import { create } from 'zustand';
import type { Alert, AlertLevel } from '@/types/inventory.types';

interface AlertFilters {
  niveau?: AlertLevel;
  hideResolved?: boolean;
  productId?: string;
}

interface AlertState {
  // State
  alerts: Alert[];
  isLoading: boolean;
  filters: AlertFilters;
  unreadCount: number;

  // Actions - Alerts
  setAlerts: (alerts: Alert[]) => void;
  addAlert: (alert: Alert) => void;
  updateAlert: (alertId: string, updates: Partial<Alert>) => void;
  removeAlert: (alertId: string) => void;
  clearAlerts: () => void;

  // Actions - Status
  markAsRead: (alertId: string) => void;
  markAsResolved: (alertId: string) => void;
  markAllAsRead: () => void;

  // Actions - UI
  setLoading: (isLoading: boolean) => void;
  setFilters: (filters: Partial<AlertFilters>) => void;
  resetFilters: () => void;

  // Computed / Helpers
  getCriticalAlerts: () => Alert[];
  getWarningAlerts: () => Alert[];
  getUnresolvedAlerts: () => Alert[];
  getAlertsByProduct: (productId: string) => Alert[];
  getFilteredAlerts: () => Alert[];
  updateUnreadCount: () => void;
}

const defaultFilters: AlertFilters = {
  niveau: undefined,
  hideResolved: true,
  productId: undefined,
};

export const useAlertStore = create<AlertState>((set, get) => ({
  // Initial state
  alerts: [],
  isLoading: false,
  filters: defaultFilters,
  unreadCount: 0,

  // Actions - Alerts
  setAlerts: (alerts) => {
    set({ alerts });
    get().updateUnreadCount();
  },

  addAlert: (alert) => {
    set((state) => ({
      alerts: [alert, ...state.alerts],
    }));
    get().updateUnreadCount();
  },

  updateAlert: (alertId, updates) => {
    set((state) => ({
      alerts: state.alerts.map((alert) =>
        alert.id === alertId ? { ...alert, ...updates } : alert
      ),
    }));
    get().updateUnreadCount();
  },

  removeAlert: (alertId) => {
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.id !== alertId),
    }));
    get().updateUnreadCount();
  },

  clearAlerts: () => {
    set({ alerts: [], unreadCount: 0 });
  },

  // Actions - Status
  markAsRead: (alertId) => {
    set((state) => ({
      alerts: state.alerts.map((alert) =>
        alert.id === alertId
          ? { ...alert, vue: true, vue_le: new Date().toISOString() }
          : alert
      ),
    }));
    get().updateUnreadCount();
  },

  markAsResolved: (alertId) => {
    set((state) => ({
      alerts: state.alerts.map((alert) =>
        alert.id === alertId
          ? {
              ...alert,
              resolue: true,
              resolue_le: new Date().toISOString(),
              vue: true,
              vue_le: alert.vue_le || new Date().toISOString(),
            }
          : alert
      ),
    }));
    get().updateUnreadCount();
  },

  markAllAsRead: () => {
    const now = new Date().toISOString();
    set((state) => ({
      alerts: state.alerts.map((alert) =>
        !alert.vue ? { ...alert, vue: true, vue_le: now } : alert
      ),
      unreadCount: 0,
    }));
  },

  // Actions - UI
  setLoading: (isLoading) => set({ isLoading }),

  setFilters: (filters) =>
    set((state) => ({
      filters: { ...state.filters, ...filters },
    })),

  resetFilters: () => set({ filters: defaultFilters }),

  // Computed / Helpers
  getCriticalAlerts: () => {
    const { alerts } = get();
    return alerts.filter(
      (alert) => alert.niveau === 'critique' && !alert.resolue
    );
  },

  getWarningAlerts: () => {
    const { alerts } = get();
    return alerts.filter(
      (alert) => alert.niveau === 'alerte' && !alert.resolue
    );
  },

  getUnresolvedAlerts: () => {
    const { alerts } = get();
    return alerts.filter((alert) => !alert.resolue);
  },

  getAlertsByProduct: (productId) => {
    const { alerts } = get();
    return alerts.filter((alert) => alert.produit_id === productId);
  },

  getFilteredAlerts: () => {
    const { alerts, filters } = get();

    return alerts.filter((alert) => {
      // Filter by niveau
      if (filters.niveau && alert.niveau !== filters.niveau) {
        return false;
      }

      // Filter resolved
      if (filters.hideResolved && alert.resolue) {
        return false;
      }

      // Filter by product
      if (filters.productId && alert.produit_id !== filters.productId) {
        return false;
      }

      return true;
    });
  },

  updateUnreadCount: () => {
    const { alerts } = get();
    const count = alerts.filter((alert) => !alert.vue && !alert.resolue).length;
    set({ unreadCount: count });
  },
}));
